/**
 * Correction snapshots (CorrectionModels.cs schema 2, shared/corrections.py): the file a saved
 * version holds, the identity a landmark is matched by, and the pure functions that turn a
 * snapshot into corrected measurement rows and the current edits back into a snapshot.
 *
 * Coordinates: `prediction_world_mm`, i.e. the file frame of measurements.json (World_*_mm).
 * The scene frame swaps y and z (theater/frame.ts); conversion happens at the edges here.
 */
import * as THREE from 'three';
import { fileToScene } from '@/theater/frame';
import type { MeasurementRow } from '@/prediction/measurements';

export interface Vec3Mm {
  x: number;
  y: number;
  z: number;
}

export type CorrectionKind = 'moved' | 'added' | 'deleted';

export interface CorrectionEntry {
  category?: string | null;
  structure: string;
  name: string;
  index: number;
  kind: CorrectionKind;
  original?: Vec3Mm | null;
  edited: Vec3Mm;
  delta?: Vec3Mm | null;
  frame_index?: number | null;
}

export interface CustomMetricEntry {
  name: string;
  shape: string;
  color?: number[] | null;
  frame_index?: number | null;
  visible?: boolean;
  points: Vec3Mm[];
}

export interface FreeMarginCurveEntry {
  structure: string;
  configuration?: string | null;
  points: Vec3Mm[];
}

export interface CorrectionSnapshot {
  schema_version: number;
  run_id?: string | null;
  project_id?: string | null;
  label?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  units: string;
  coordinate_space: string;
  corrections: CorrectionEntry[];
  custom_metrics: CustomMetricEntry[];
  free_margins: FreeMarginCurveEntry[];
  [extra: string]: unknown;
}

export interface CorrectionVersionInfo {
  slug: string;
  label: string;
  count: number;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

/** Below this displacement a point counts as unmoved (CorrectionMath epsilon, mm). */
export const MOVE_EPSILON_MM = 0.01;
/** Entries whose stored original drifted further than this from the live AI point are skipped. */
export const DRIFT_TOLERANCE_MM = 1.0;

export function emptySnapshot(): CorrectionSnapshot {
  return { schema_version: 2, units: 'mm', coordinate_space: 'prediction_world_mm', corrections: [], custom_metrics: [], free_margins: [] };
}

/** CorrectionStorage.Slugify (mirrors shared/corrections.py). */
export function slugify(label: string): string {
  let out = '';
  let pending = false;
  for (const c of (label ?? '').toLowerCase()) {
    if ((c >= 'a' && c <= 'z') || (c >= '0' && c <= '9')) {
      if (pending && out.length) out += '-';
      pending = false;
      out += c;
    } else pending = true;
  }
  return out;
}

// ------------------------------------------------------------- identities

/** The landmark identity of a measurement row: category / structure / name / index. */
export interface Identity {
  category: string | null;
  structure: string;
  name: string;
  index: number;
}

export function identityKey(id: Identity, frame: number | null = null): string {
  return `${id.category ?? ''}|${id.structure}|${id.name}|${id.index}|${frame ?? '-'}`;
}

export function rowIdentity(r: MeasurementRow): Identity {
  return { category: r.Category, structure: r.Structure, name: r.Name ?? '', index: r.Index ?? 0 };
}

export function entryIdentity(e: CorrectionEntry): Identity {
  return { category: e.category ?? null, structure: e.structure, name: e.name, index: e.index };
}

export function sameIdentity(a: Identity, b: Identity): boolean {
  return (a.category ?? null) === (b.category ?? null) && a.structure === b.structure && a.name === b.name && a.index === b.index;
}

export function vecMm(v: Vec3Mm): THREE.Vector3 {
  return fileToScene(v.x, v.y, v.z);
}

export function toVecMm(scene: THREE.Vector3): Vec3Mm {
  // scene (x, y, z) -> file (x, z, y)
  return { x: scene.x, y: scene.z, z: scene.y };
}

function dist(a: Vec3Mm, b: Vec3Mm): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function rowPos(r: MeasurementRow): Vec3Mm | null {
  if (typeof r.World_X_mm !== 'number' || typeof r.World_Y_mm !== 'number' || typeof r.World_Z_mm !== 'number') return null;
  return { x: r.World_X_mm, y: r.World_Y_mm, z: r.World_Z_mm };
}

/** Structures whose entries are session-only on the desktop and never applied. */
const IGNORED_STRUCTURES = new Set(['Angles']);
const CENTERLINE_STRUCTURE = 'CenterLine';

// ------------------------------------------------------------- apply

export interface AppliedCorrections {
  rows: MeasurementRow[];
  /** Entries applied, keyed by identityKey(id, frame). */
  applied: Map<string, CorrectionEntry>;
  /** Entries skipped: no matching AI row, or the AI point drifted (pipeline re-run). */
  skipped: { entry: CorrectionEntry; reason: 'missing' | 'drift' }[];
  /** Free margins replaced from the snapshot (structure -> ordered scene-mm points). */
  margins: Map<string, THREE.Vector3[]>;
  /** Centerline edits to replay on the centerline polyline. */
  centerline: CorrectionEntry[];
}

/**
 * ReapplyPendingMovedCorrections, restricted to what the row file carries: "moved" entries of
 * Point / RingPoint / CurvePoint rows matched by identity (and frame when the entry carries
 * one), skipped when the AI original drifted by more than 1 mm. CenterLine entries are
 * returned for the centerline replay; free margins are returned as replacement polylines.
 */
export function applySnapshot(aiRows: MeasurementRow[], snapshot: CorrectionSnapshot | null, activeConfiguration: string | null): AppliedCorrections {
  const rows = aiRows.map((r) => ({ ...r }));
  const applied = new Map<string, CorrectionEntry>();
  const skipped: AppliedCorrections['skipped'] = [];
  const margins = new Map<string, THREE.Vector3[]>();
  const centerline: CorrectionEntry[] = [];
  if (!snapshot) return { rows, applied, skipped, margins, centerline };

  // index rows by identity (+ frame) for the moved lookups
  const byKey = new Map<string, number[]>();
  rows.forEach((r, i) => {
    if (r.Category !== 'Point' && r.Category !== 'RingPoint' && r.Category !== 'CurvePoint') return;
    const id = rowIdentity(r);
    const push = (k: string) => {
      const list = byKey.get(k) ?? [];
      list.push(i);
      byKey.set(k, list);
    };
    push(identityKey(id, r.Frame_Index ?? null));
    push(identityKey(id));
  });

  for (const e of snapshot.corrections) {
    if (e.structure === CENTERLINE_STRUCTURE) {
      centerline.push(e);
      continue;
    }
    if (IGNORED_STRUCTURES.has(e.structure) || e.kind !== 'moved' || !e.category) continue;
    const id = entryIdentity(e);
    const frame = e.frame_index ?? null;
    const candidates = byKey.get(identityKey(id, frame)) ?? (frame === null ? byKey.get(identityKey(id)) : undefined) ?? [];
    const matched = candidates.filter((i) => (frame === null ? true : (rows[i].Frame_Index ?? null) === frame));
    if (matched.length === 0) {
      skipped.push({ entry: e, reason: 'missing' });
      continue;
    }
    for (const i of matched) {
      const live = rowPos(rows[i]);
      if (!live) continue;
      if (e.original && dist(e.original, live) > DRIFT_TOLERANCE_MM) {
        skipped.push({ entry: e, reason: 'drift' });
        continue;
      }
      rows[i] = { ...rows[i], World_X_mm: e.edited.x, World_Y_mm: e.edited.y, World_Z_mm: e.edited.z };
      applied.set(identityKey(id, rows[i].Frame_Index ?? null), e);
      if (frame === null) break; // a legacy entry seeds one row
    }
  }

  // free margins: the reading's entries replace the curves; others stay in the snapshot
  const reading = (activeConfiguration ?? '').toLowerCase();
  for (const m of snapshot.free_margins) {
    const cfg = (m.configuration ?? '').toLowerCase();
    if (cfg && reading && cfg !== reading) continue;
    if (m.points.length >= 2) margins.set(m.structure, m.points.map(vecMm));
  }
  return { rows, applied, skipped, margins, centerline };
}

// ------------------------------------------------------------- build

export interface PointEdit {
  identity: Identity;
  frame: number | null;
  original: Vec3Mm; // the AI position
  edited: Vec3Mm;
}

export interface BuildInput {
  runId: string;
  projectId: string | null;
  label: string | null;
  /** Live landmark positions that differ from the AI prediction. */
  points: PointEdit[];
  /** Every free margin, as drawn, for both readings (structure, configuration, scene points). */
  margins: { structure: string; configuration: string | null; points: THREE.Vector3[] }[];
  /** CenterLine entries: moved / added / deleted, already in mm. */
  centerline: CorrectionEntry[];
  /** Carried over from the loaded version (the web app has no draw tools yet). */
  customMetrics: CustomMetricEntry[];
  /** Entries of the loaded version that did not match this run (kept so nothing is lost). */
  carried: CorrectionEntry[];
}

/** CorrectionMath.TryBuildMovedEntry over the live edits, plus margins and the centerline. */
export function buildSnapshot(input: BuildInput): CorrectionSnapshot {
  const corrections: CorrectionEntry[] = [];
  for (const p of input.points) {
    if (dist(p.original, p.edited) < MOVE_EPSILON_MM) continue;
    const entry: CorrectionEntry = {
      category: p.identity.category,
      structure: p.identity.structure,
      name: p.identity.name,
      index: p.identity.index,
      kind: 'moved',
      original: { ...p.original },
      edited: { ...p.edited },
      delta: { x: p.edited.x - p.original.x, y: p.edited.y - p.original.y, z: p.edited.z - p.original.z },
    };
    if (p.frame !== null) entry.frame_index = p.frame;
    corrections.push(entry);
  }
  corrections.push(...input.centerline);
  corrections.push(...input.carried);
  return {
    schema_version: 2,
    run_id: input.runId,
    project_id: input.projectId,
    label: input.label,
    units: 'mm',
    coordinate_space: 'prediction_world_mm',
    corrections,
    custom_metrics: input.customMetrics,
    free_margins: input.margins.map((m) => ({ structure: m.structure, configuration: m.configuration, points: m.points.map(toVecMm) })),
  };
}

/** CorrectionMath.NearestSegmentStartIndex: index i of the segment [i, i+1] closest to target. */
export function nearestSegmentStartIndex(polyline: THREE.Vector3[], target: THREE.Vector3): number {
  if (polyline.length < 2) return 0;
  let best = 0;
  let bestD = Infinity;
  const line = new THREE.Line3();
  const tmp = new THREE.Vector3();
  for (let i = 0; i + 1 < polyline.length; i++) {
    line.set(polyline[i], polyline[i + 1]);
    line.closestPointToPoint(target, true, tmp);
    const d = tmp.distanceToSquared(target);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
